import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Trash2, Check, LogOut, RefreshCw, CalendarDays, UserPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("amani_token", data.token);
      onLogin();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a0a0a] px-6">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
        <p className="mb-3 text-[10px] uppercase tracking-[0.4em] text-white/40">Amani Talent Agency</p>
        <h1 className="font-serif-lux mb-12 text-4xl font-light tracking-tight text-white">Admin Access</h1>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          data-testid="admin-email"
          className="mb-8 w-full rounded-none border-0 border-b border-white/20 bg-transparent py-4 text-white placeholder-white/30 outline-none focus:border-white"
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          data-testid="admin-password"
          className="mb-12 w-full rounded-none border-0 border-b border-white/20 bg-transparent py-4 text-white placeholder-white/30 outline-none focus:border-white"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-button"
          className="w-full rounded-none bg-white py-4 text-[11px] uppercase tracking-[0.3em] text-black transition-colors hover:bg-white/80 disabled:opacity-50"
        >
          {loading ? "Signing in" : "Enter"}
        </button>
      </form>
    </div>
  );
}

function Inbox({ onLogout }) {
  const [items, setItems] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [tab, setTab] = useState("messages");
  const [loading, setLoading] = useState(true);

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("amani_token")}` },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subs, books, apps] = await Promise.all([
        axios.get(`${API}/admin/submissions`, authHeader()),
        axios.get(`${API}/admin/bookings`, authHeader()),
        axios.get(`${API}/admin/applications`, authHeader()),
      ]);
      setItems(subs.data);
      setBookings(books.data);
      setApplications(apps.data);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const removeApplication = async (id) => {
    await axios.delete(`${API}/admin/applications/${id}`, authHeader());
    setApplications((p) => p.filter((a) => a.id !== id));
    toast.success("Application deleted");
  };

  const removeBooking = async (id) => {
    await axios.delete(`${API}/admin/bookings/${id}`, authHeader());
    setBookings((p) => p.filter((b) => b.id !== id));
    toast.success("Booking deleted");
  };

  const setBookingStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${id}/status`, { status }, authHeader());
      setBookings((p) => p.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(
        status === "confirmed"
          ? "Booking confirmed — client notified by email."
          : `Booking marked ${status}.`
      );
    } catch {
      toast.error("Could not update status.");
    }
  };

  const markRead = async (id) => {
    await axios.patch(`${API}/admin/submissions/${id}/read`, {}, authHeader());
    setItems((p) => p.map((i) => (i.id === id ? { ...i, read: true } : i)));
  };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/submissions/${id}`, authHeader());
    setItems((p) => p.filter((i) => i.id !== id));
    toast.success("Submission deleted");
  };

  const unread = items.filter((i) => !i.read).length;

  return (
    <div className="min-h-screen bg-[#0a0a0a] px-6 py-10 text-white md:px-12">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between border-b border-white/10 pb-8">
          <div>
            <p className="mb-2 text-[10px] uppercase tracking-[0.4em] text-white/40">Amani Talent Agency</p>
            <h1 className="font-serif-lux text-4xl font-light tracking-tight">Inbox</h1>
          </div>
          <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.2em]">
            <span className="text-white/50" data-testid="inbox-count">
              {tab === "messages"
                ? `${items.length} messages · ${unread} new`
                : tab === "bookings"
                ? `${bookings.length} bookings`
                : `${applications.length} applications`}
            </span>
            <button onClick={load} data-testid="inbox-refresh" className="flex items-center gap-2 text-white/60 hover:text-white">
              <RefreshCw size={14} />
            </button>
            <button onClick={onLogout} data-testid="admin-logout" className="flex items-center gap-2 text-white/60 hover:text-white">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex gap-8 text-[11px] uppercase tracking-[0.25em]">
          <button
            onClick={() => setTab("messages")}
            data-testid="tab-messages"
            className={`pb-2 transition-colors ${tab === "messages" ? "border-b border-white text-white" : "text-white/40 hover:text-white/70"}`}
          >
            Messages
          </button>
          <button
            onClick={() => setTab("bookings")}
            data-testid="tab-bookings"
            className={`pb-2 transition-colors ${tab === "bookings" ? "border-b border-white text-white" : "text-white/40 hover:text-white/70"}`}
          >
            Bookings
          </button>
          <button
            onClick={() => setTab("applications")}
            data-testid="tab-applications"
            className={`pb-2 transition-colors ${tab === "applications" ? "border-b border-white text-white" : "text-white/40 hover:text-white/70"}`}
          >
            Applications
          </button>
        </div>

        {loading ? (
          <p className="py-20 text-center text-white/40">Loading…</p>
        ) : tab === "messages" ? (
          items.length === 0 ? (
            <div className="flex flex-col items-center py-32 text-white/40">
              <Mail size={28} strokeWidth={1} />
              <p className="mt-4 text-sm tracking-wide">No messages yet.</p>
            </div>
          ) : (
            <div className="mt-4 border border-white/10" data-testid="inbox-table">
              {items.map((it) => (
                <div
                  key={it.id}
                  data-testid={`inbox-row-${it.id}`}
                  className={`grid grid-cols-1 gap-2 border-b border-white/10 px-6 py-6 last:border-0 md:grid-cols-[1fr_1.4fr_auto] md:items-start md:gap-6 ${
                    it.read ? "opacity-60" : ""
                  }`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      {!it.read && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                      <p className="text-base font-light">{it.name}</p>
                    </div>
                    <p className="mt-1 text-xs tracking-wide text-white/50">{it.email}</p>
                    <p className="mt-2 inline-block border border-white/15 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-white/60">
                      {it.interest}
                    </p>
                  </div>
                  <p className="text-sm font-light leading-relaxed text-white/80">{it.message}</p>
                  <div className="flex items-center gap-4 md:flex-col md:items-end md:gap-3">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{formatDate(it.created_at)}</span>
                    <div className="flex gap-3">
                      {!it.read && (
                        <button onClick={() => markRead(it.id)} data-testid={`mark-read-${it.id}`} title="Mark read" className="text-white/50 hover:text-white">
                          <Check size={16} />
                        </button>
                      )}
                      <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} title="Delete" className="text-white/50 hover:text-white">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : tab === "bookings" ? (
          bookings.length === 0 ? (
          <div className="flex flex-col items-center py-32 text-white/40">
            <CalendarDays size={28} strokeWidth={1} />
            <p className="mt-4 text-sm tracking-wide">No bookings yet.</p>
          </div>
        ) : (
          <div className="mt-4 border border-white/10" data-testid="bookings-table">
            {bookings.map((b) => (
              <div
                key={b.id}
                data-testid={`booking-row-${b.id}`}
                className="grid grid-cols-1 gap-2 border-b border-white/10 px-6 py-6 last:border-0 md:grid-cols-[1fr_1fr_auto] md:items-start md:gap-6"
              >
                <div>
                  <p className="text-base font-light">{b.name}</p>
                  <p className="mt-1 text-xs tracking-wide text-white/50">{b.email}</p>
                  <p className="mt-2 inline-block border border-white/15 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-white/70">
                    {b.service}
                  </p>
                </div>
                <div className="text-sm font-light text-white/80">
                  <p>{b.date} · {b.time}</p>
                  {b.notes && <p className="mt-2 text-xs leading-relaxed text-white/50">{b.notes}</p>}
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span
                      data-testid={`booking-status-${b.id}`}
                      className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] ${
                        b.status === "confirmed"
                          ? "bg-white text-black"
                          : b.status === "completed"
                          ? "border border-white/40 text-white/70"
                          : "border border-white/15 text-white/40"
                      }`}
                    >
                      {b.status || "pending"}
                    </span>
                    {b.status !== "confirmed" && b.status !== "completed" && (
                      <button
                        onClick={() => setBookingStatus(b.id, "confirmed")}
                        data-testid={`confirm-booking-${b.id}`}
                        className="text-[10px] uppercase tracking-[0.2em] text-white/60 underline underline-offset-4 hover:text-white"
                      >
                        Confirm
                      </button>
                    )}
                    {b.status !== "completed" && (
                      <button
                        onClick={() => setBookingStatus(b.id, "completed")}
                        data-testid={`complete-booking-${b.id}`}
                        className="text-[10px] uppercase tracking-[0.2em] text-white/60 underline underline-offset-4 hover:text-white"
                      >
                        Mark complete
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 md:flex-col md:items-end md:gap-3">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{formatDate(b.created_at)}</span>
                  <button onClick={() => removeBooking(b.id)} data-testid={`delete-booking-${b.id}`} title="Delete" className="text-white/50 hover:text-white">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
        ) : applications.length === 0 ? (
          <div className="flex flex-col items-center py-32 text-white/40">
            <UserPlus size={28} strokeWidth={1} />
            <p className="mt-4 text-sm tracking-wide">No applications yet.</p>
          </div>
        ) : (
          <div className="mt-4 border border-white/10" data-testid="applications-table">
            {applications.map((a) => (
              <div
                key={a.id}
                data-testid={`application-row-${a.id}`}
                className="grid grid-cols-1 gap-2 border-b border-white/10 px-6 py-6 last:border-0 md:grid-cols-[1fr_1.4fr_auto] md:items-start md:gap-6"
              >
                <div>
                  <p className="text-base font-light">{a.name}</p>
                  <p className="mt-1 text-xs tracking-wide text-white/50">{a.email}</p>
                </div>
                <p className="text-sm font-light leading-relaxed text-white/80">{a.socials}</p>
                <div className="flex items-center gap-4 md:flex-col md:items-end md:gap-3">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{formatDate(a.created_at)}</span>
                  <button onClick={() => removeApplication(a.id)} data-testid={`delete-application-${a.id}`} title="Delete" className="text-white/50 hover:text-white">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("amani_token"));

  const logout = () => {
    localStorage.removeItem("amani_token");
    setAuthed(false);
  };

  return authed ? <Inbox onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />;
}
